/**
 * useT — lightweight translation hook for DysTest i18n
 *
 * Returns a `t(key)` function that looks up the translated string for the
 * currently selected language (from LanguageContext). Falls back to English
 * for unsupported language codes or missing keys.
 *
 * Usage:
 *   const { t } = useT();
 *   <p>{t('story1Title')}</p>
 *
 * For messages with a count placeholder {n}:
 *   t('backCountPlural').replace('{n}', String(backCount))
 */

import { useLanguage } from '../contexts/LanguageContext';
import { getTranslations, type Translations } from '../i18n';
import { devWarn } from '@/utils/core/devLog';

export interface UseTResult {
  /** Look up a translation key for the current language. Falls back to English. */
  t: (key: keyof Translations) => string;
  /** Current language code (e.g. 'en', 'nl', 'fr', 'de', 'hi'). */
  lang: string;
}

export function useT(): UseTResult {
  const { currentLanguage } = useLanguage();
  const dict = getTranslations(currentLanguage);

  const t = (key: keyof Translations): string => {
    const value = dict[key];
    if (value === undefined || value === '') {
      if (process.env['NODE_ENV'] === 'development') {
        devWarn(`[useT] Missing translation key "${key}" for lang "${currentLanguage}"`);
      }
      // Fallback to English
      const enDict = getTranslations('en');
      return enDict[key] ?? key;
    }
    return value;
  };

  return { t, lang: currentLanguage };
}

export default useT;
